import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// Blocs « en plus » d'un thème : chaque thème peut proposer des
// sections supplémentaires que le marchand remplit lui-même.
// Chaque champ est facultatif et peut être masqué indépendamment,
// et le bloc entier peut être rendu invisible.
// ─────────────────────────────────────────────────────────────

export type ExtraBlockLayout = 'image-left' | 'image-right' | 'center' | 'full';

export interface ThemeExtraBlock {
  id: string;
  title: string;
  description: string;
  buttonLabel: string;
  buttonHref: string;
  image: string;
  backgroundImage: string;
  layout: ExtraBlockLayout;
  hideTitle: boolean;
  hideDescription: boolean;
  hideButton: boolean;
  hideImage: boolean;
  hidden: boolean;
}

export const LAYOUT_OPTIONS: { value: ExtraBlockLayout; label: string; icon: string }[] = [
  { value: 'image-left', label: 'Image à gauche', icon: 'ri-layout-left-line' },
  { value: 'image-right', label: 'Image à droite', icon: 'ri-layout-right-line' },
  { value: 'center', label: 'Centré (sans image)', icon: 'ri-layout-row-line' },
  { value: 'full', label: 'Bannière pleine largeur', icon: 'ri-layout-top-line' },
];

export function emptyExtraBlock(index = 0): ThemeExtraBlock {
  return {
    id: `bloc-${Date.now()}-${index}`,
    title: '',
    description: '',
    buttonLabel: '',
    buttonHref: '',
    image: '',
    backgroundImage: '',
    layout: 'image-left',
    hideTitle: false,
    hideDescription: false,
    hideButton: false,
    hideImage: false,
    hidden: false,
  };
}

function asString(value: unknown): string {
  return value === undefined || value === null ? '' : String(value);
}

function asBool(value: unknown): boolean {
  return value === true;
}

/** Normalise un bloc relu depuis la base (tolérant aux champs manquants). */
export function normalizeExtraBlock(raw: unknown, index = 0): ThemeExtraBlock {
  const row = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const layout = asString(row.layout) as ExtraBlockLayout;
  return {
    id: asString(row.id) || `bloc-${index}`,
    title: asString(row.title),
    description: asString(row.description),
    buttonLabel: asString(row.buttonLabel),
    buttonHref: asString(row.buttonHref),
    image: asString(row.image),
    backgroundImage: asString(row.backgroundImage),
    layout: LAYOUT_OPTIONS.some((l) => l.value === layout) ? layout : 'image-left',
    hideTitle: asBool(row.hideTitle),
    hideDescription: asBool(row.hideDescription),
    hideButton: asBool(row.hideButton),
    hideImage: asBool(row.hideImage),
    hidden: asBool(row.hidden),
  };
}

/** Un bloc est-il réellement affichable (au moins un contenu visible) ? */
export function isExtraBlockRenderable(block: ThemeExtraBlock): boolean {
  if (block.hidden) return false;
  const hasTitle = !block.hideTitle && block.title.trim() !== '';
  const hasDescription = !block.hideDescription && block.description.trim() !== '';
  const hasButton = !block.hideButton && block.buttonLabel.trim() !== '';
  const hasImage = !block.hideImage && block.image.trim() !== '';
  return hasTitle || hasDescription || hasButton || hasImage;
}

/** Charge les blocs supplémentaires du commerce (blocs du thème courant). */
export async function loadThemeExtraBlocks(idcommerce: number): Promise<ThemeExtraBlock[]> {
  if (!idcommerce) return [];
  const { data, error } = await supabase
    .from('theme_extra_blocks')
    .select('blocks')
    .eq('idcommerce', idcommerce)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (error) throw error;
  const raw = (data || [])[0]?.blocks;
  if (!Array.isArray(raw)) return [];
  return raw.map((item, index) => normalizeExtraBlock(item, index));
}

/** Enregistre les blocs du commerce pour un thème donné. */
export async function saveThemeExtraBlocks(
  idcommerce: number,
  idtheme: number,
  blocks: ThemeExtraBlock[],
): Promise<void> {
  const clean = blocks.map((block, index) => normalizeExtraBlock(block, index));
  const { data, error } = await supabase
    .from('theme_extra_blocks')
    .select('id')
    .eq('idcommerce', idcommerce)
    .eq('idtheme', idtheme)
    .maybeSingle();
  if (error) throw error;
  if (data?.id) {
    const { error: updateError } = await supabase
      .from('theme_extra_blocks')
      .update({ blocks: clean, updated_at: new Date().toISOString() })
      .eq('id', data.id);
    if (updateError) throw updateError;
    return;
  }
  const { error: insertError } = await supabase
    .from('theme_extra_blocks')
    .insert({ idcommerce, idtheme, blocks: clean });
  if (insertError) throw insertError;
}